/**
 * Environment variable validation and configuration
 * Ensures all required environment variables are present and valid
 */

interface EnvConfig {
  AUTH0_DOMAIN: string
  AUTH0_CLIENT_ID: string
  AUTH0_REDIRECT_URI: string
  AUTH0_AUDIENCE: string
  APP_ORIGIN: string
  API_BASE_URL: string
  API_OPENAPI_URL?: string
  ASSISTANT_WS_URL?: string
}

/**
 * Gets environment variable from multiple sources
 * 1. Runtime config (window.ENV) - for containerized deployments
 * 2. Build-time env variables (import.meta.env) - for development
 */
function getEnvVar(key: string, fallback?: string): string | undefined {
  // Check runtime config first (containerized deployment)
  if (typeof window !== 'undefined' && (window as any).ENV) {
    const runtimeValue = (window as any).ENV[key]
    if (runtimeValue && runtimeValue !== 'placeholder') {
      return runtimeValue
    }
  }
  
  // Check build-time environment variables
  const buildTimeValue = import.meta.env[key]
  if (buildTimeValue && buildTimeValue !== 'placeholder') {
    return buildTimeValue
  }
  
  return fallback
}

/**
 * Validates and returns environment configuration
 * Throws error if required variables are missing or invalid
 */
export function getEnvConfig(): EnvConfig {
  const errors: string[] = []

  // Required Auth0 configuration
  const AUTH0_DOMAIN = getEnvVar('VITE_AUTH0_DOMAIN')
  const AUTH0_CLIENT_ID = getEnvVar('VITE_AUTH0_CLIENT_ID')
  const AUTH0_REDIRECT_URI = getEnvVar('VITE_AUTH0_REDIRECT_URI')
  const AUTH0_AUDIENCE = getEnvVar('VITE_AUTH0_AUDIENCE')
  
  // API configuration with fallback
  const API_BASE_URL = getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000')!
  const API_OPENAPI_URL = getEnvVar('VITE_API_OPENAPI_URL')
  const ASSISTANT_WS_URL = getEnvVar('VITE_ASSISTANT_WS_URL')

  // Validate required Auth0 variables
  if (!AUTH0_DOMAIN) {
    errors.push('VITE_AUTH0_DOMAIN is required')
  } else if (!isValidDomain(AUTH0_DOMAIN)) {
    errors.push('VITE_AUTH0_DOMAIN must be a valid domain')
  }

  if (!AUTH0_CLIENT_ID) {
    errors.push('VITE_AUTH0_CLIENT_ID is required')
  }

  if (!AUTH0_REDIRECT_URI) {
    errors.push('VITE_AUTH0_REDIRECT_URI is required')
  } else if (!isValidUrl(AUTH0_REDIRECT_URI)) {
    errors.push('VITE_AUTH0_REDIRECT_URI must be a valid URL')
  }

  if (!AUTH0_AUDIENCE) {
    errors.push('VITE_AUTH0_AUDIENCE is required')
  } else if (!isValidUrl(AUTH0_AUDIENCE)) {
    errors.push('VITE_AUTH0_AUDIENCE must be a valid URL or identifier')
  }

  // Validate API URL
  if (!isValidUrl(API_BASE_URL)) {
    errors.push('VITE_API_BASE_URL must be a valid URL')
  }

  if (errors.length > 0) {
    throw new Error(`Environment configuration errors:\n${errors.join('\n')}`)
  }

  return {
    AUTH0_DOMAIN: AUTH0_DOMAIN!,
    AUTH0_CLIENT_ID: AUTH0_CLIENT_ID!,
    AUTH0_REDIRECT_URI: AUTH0_REDIRECT_URI!,
    AUTH0_AUDIENCE: AUTH0_AUDIENCE!,
    APP_ORIGIN: new URL(AUTH0_REDIRECT_URI!).origin,
    API_BASE_URL,
    API_OPENAPI_URL,
    ASSISTANT_WS_URL
  }
}

/**
 * Validates if a string is a valid domain
 */
function isValidDomain(domain: string): boolean {
  try {
    // Auth0 domains should be valid hostnames
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-_]*\.auth0\.com$|^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/
    return domainRegex.test(domain)
  } catch {
    return false
  }
}

/**
 * Validates if a string is a valid URL
 */
function isValidUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Safe environment configuration with validation
 */
export const env = getEnvConfig()